<template>
  <app-layout>
    <template #header>
      <div class="flex justify-between items-center">
        <h2 class="font-semibold text-xl text-gray-800 leading-tight">API Tokens</h2>
      </div>
    </template>

    <div class="max-w-7xl mx-auto flex mb-12">
      <div class="w-full p-2 sm:px-6 lg:px-8">
        <div class="bg-white shadow-xl sm:rounded-lg p-4 sm:p-6 lg:p-8">
          <div class="mt-5">
            <p>
              Use API tokens to connect TextSpot to your favorite apps like Google Calendar, Facebook, Eventbrite and thousands more with Zapier. Build "Zaps" which automatically send messages based on triggers you choose, or use TextSpot triggers to run actions in other apps.
            </p>
            <h5 class="font-bold mt-5">Connect to Zapier in a few easy steps:</h5>
            <ol class="list-inside list-decimal mt-3 ml-10 space-y-1">
              <li>
                <Link class="text-blue-500 hover:text-blue-600" href="/zapier-integration">Login</Link>
                to Zapier and connect TextSpot as a new app.
              </li>
              <li>
                A popup that says "Allow Zapier to access your TextSpot Account?" will show up:
                <ul class="ml-10 space-y-1">
                  <li>
                    <span class="font-bold">Personal Access Token:</span>
                    <i>Generate a new token below and copy/paste it into the "Personal Access Token" field within the Zapier popup.</i>
                  </li>
                  <li>Then click "Yes, continue"</li>
                </ul>
              </li>
              <li>
                You're ready to build your first Zap! Automate your workflow by connecting TextSpot to the apps you use most.
              </li>
            </ol>
            <div class="border-t mt-8"></div>
            <!-- New table -->
            <div class="mt-8">
              <div class="sm:flex sm:items-center">
                <div class="sm:flex-auto">
                  <h1 class="text-xl font-semibold text-gray-900">Personal Access Tokens</h1>
                  <p class="mt-2 text-sm text-gray-700">
                    Tokens you have generated that can be used to access the TextSpot API.
                  </p>
                </div>
                <div class="mt-8 sm:mt-0 sm:ml-16 sm:flex-none">
                  <form @submit.prevent="store">
                    <button
                      type="submit"
                      class="inline-flex items-center px-4 py-2 bg-orange-400 border border-transparent rounded-md font-bold text-xs text-white uppercase tracking-widest hover:bg-orange-500 active:bg-orange-600 focus:outline-none focus:border-orange-600 focus:ring-orange transition ease-in-out duration-150"
                    >
                      Generate new token
                    </button>
                  </form>
                </div>
              </div>
              <div v-if="$page.props.flash.token">
                <div class="rounded-md bg-blue-100 p-4 mt-4">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5 text-blue-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </div>
                    <div class="ml-3 flex-1 md:flex md:justify-between">
                      <p class="text-sm text-blue-700">
                        Make sure to copy your personal access token now. You won’t be able to see it again!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="mt-4 flex flex-col">
                <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div class="inline-block min-w-full p-4 align-middle md:px-6 lg:px-8">
                    <div class="overflow-hidden shadow border border-gray-400 md:rounded-lg">
                      <table class="min-w-full divide-y divide-gray-300">
                        <thead class="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            class="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm text-gray-900 sm:pl-6"
                          >
                            Name
                          </th>
                          <th scope="col" class="whitespace-nowrap px-2 py-3.5 text-left text-sm text-gray-900">
                            Last Used
                          </th>
                          <th scope="col" class="relative whitespace-nowrap py-3.5 pl-3 pr-4 sm:pr-6">
                            <span class="sr-only">Actions</span>
                          </th>
                        </tr>
                        </thead>
                        <tbody v-if="tokens.length > 0" class="divide-y divide-gray-200 bg-white">
                        <tr v-if="$page.props.flash.token">
                          <td colspan="3" class="whitespace-nowrap py-2 pl-4 pr-3 text-green-500 sm:pl-6">
                            <div class="flex items-center text-lg space-x-2">
                              <div>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  class="h-5 w-5 text-green-500"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fill-rule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clip-rule="evenodd"
                                  />
                                </svg>
                              </div>
                              <div class="bg-green-200 rounded px-1.5 py-0.5 text-base text-green-700">
                                {{ $page.props.flash.token }}
                              </div>
                              <button
                                type="button"
                                class="inline-flex items-center text-gray-500 hover:text-gray-700 items-center border border-transparent rounded-full focus:outline-none"
                                v-clipboard:copy="$page.props.flash.token"
                                v-clipboard:success="onCopy"
                              >
                                <svg
                                  v-if="copied == false"
                                  xmlns="http://www.w3.org/2000/svg"
                                  class="h-5 w-5"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V6.414A2 2 0 0016.414 5L14 2.586A2 2 0 0012.586 2H9z"
                                  />
                                  <path d="M3 8a2 2 0 012-2v10h8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                                </svg>
                                <svg
                                  v-else
                                  xmlns="http://www.w3.org/2000/svg"
                                  class="h-5 w-5 text-green-500"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                  <path
                                    fill-rule="evenodd"
                                    d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clip-rule="evenodd"
                                  />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                        <tr v-for="token in tokens" :key="token.id">
                          <td class="whitespace-nowrap py-2 pl-4 pr-3 text-gray-900 sm:pl-6">
                            {{ token.name }}
                          </td>
                          <td
                            v-if="token.last_used_at"
                            v-text="formatLastUsedAt(token)"
                            class="whitespace-nowrap px-2 py-2 text-sm text-gray-900"
                          ></td>
                          <td v-else>Never used</td>
                          <td class="relative whitespace-nowrap py-2 pl-3 pr-4 text-right text-sm sm:pr-6">
                            <form @submit.prevent="destroy(token)">
                              <button
                                type="submit"
                                class="inline-flex items-center px-2.5 py-1.5 bg-red-400 border border-transparent rounded-md font-bold text-xs text-white uppercase tracking-widest hover:bg-red-500 active:bg-red-600 focus:outline-none focus:border-red-600 focus:ring-red transition ease-in-out duration-150"
                              >
                                Revoke
                              </button>
                            </form>
                          </td>
                        </tr>
                        </tbody>
                        <tbody v-else>
                        <tr>
                          <td class="whitespace-nowrap py-2 pl-4 pr-3 text-gray-900 sm:pl-6">
                            No tokens generated...
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- End new table -->
            <!-- <h3 class="text-lg my-6">Personal Access Tokens</h3>
            <form @submit.prevent="store">
              <button
                type="submit"
                class="inline-flex items-center px-4 py-2 bg-orange-400 border border-transparent rounded-md font-bold text-xs text-white uppercase tracking-widest hover:bg-orange-500 active:bg-orange-600 focus:outline-none focus:border-orange-600 focus:ring-orange transition ease-in-out duration-150"
              >
                Generate new token
              </button>
            </form>
            <table v-if="tokens.length > 0">
              <tr>
                <th>Token</th>
                <th>Last Used</th>
                <th></th>
              </tr>
              <tr v-if="$page.props.flash.token">
                <td colspan="2" v-text="$page.props.flash.token"></td>
              </tr>
              <tr v-for="token in tokens">
                <td v-text="token.name"></td>
                <td v-if="token.last_used_at" v-text="token.last_used_at"></td>
                <td v-else>Never used</td>
                <td>
                  <form @submit.prevent="destroy(token)">
                    <button type="submit">Revoke</button>
                  </form>
                </td>
              </tr>
            </table>

            <span v-else>You have no tokens</span> -->
          </div>
        </div>
      </div>
    </div>
  </app-layout>
</template>

<script>
import AppLayout from "../../Layouts/AppLayout";
import moment from 'moment';
export default {
  props: {
    tokens: {
      default: undefined,
    },
    csrf: {
      default: undefined,
    },
  },
  components: {
    AppLayout,
  },
  data() {
    return {
      copied: false,
      transactions: [
        {
          id: "AAPS0L",
          company: "Chase & Co.",
          share: "CAC",
          commission: "+$4.37",
          price: "$3,509.00",
          quantity: "12.00",
          netAmount: "$4,397.00",
        },
      ],
    };
  },
  methods: {
    formatLastUsedAt(token) {
      return moment(token.last_used_at).format("MMM Do, YYYY");
    },
    store() {
      this.$inertia.post(route("zapier-integration.index"), [], {
        preserveScroll: true,
        onError: (errors) => {},
      });
    },
    destroy(token) {
      if (
        window.confirm(
          "Are you sure you want to delete this token?\n\nAny applications or scripts using this token will no longer be able to access the TextSpot API. You cannot undo this action."
        )
      ) {
        this.$inertia.delete(route("zapier-integration.destroy", token.id), {
          preserveScroll: true,
          onError: (errors) => {},
        });
      }
    },
    onCopy: function (e) {
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 3000);
    },
  },
};
</script>
